import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * Listens for variant update events (swatch changes) and quantity-selector
 * update events (quantity +/-) to keep the displayed price in sync.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this.closestSection = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.updateQuantityPrice);
  }

  disconnectedCallback() {
    if (!this.closestSection) return;
    this.closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.updateQuantityPrice);
  }

  /**
   * Updates the price and volume pricing note on variant change.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Keep our price-per-unit data in sync with the newly selected variant
    if (newProductPrice.dataset.unitPrice) this.dataset.unitPrice = newProductPrice.dataset.unitPrice;
    if (newProductPrice.dataset.variantId) this.dataset.variantId = newProductPrice.dataset.variantId;
    if (newProductPrice.dataset.moneyFormat) this.dataset.moneyFormat = newProductPrice.dataset.moneyFormat;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };

  /**
   * Updates the displayed price when the quantity selector changes,
   * multiplying the unit price by the selected quantity.
   * @param {CustomEvent} event - The quantity-selector update event.
   */
  updateQuantityPrice = (event) => {
    // Ignore cart-line quantity selectors (cart page) - only react to PDP selector
    if (event.detail.cartLine !== undefined) return;

    const unitPrice = parseInt(this.dataset.unitPrice, 10);
    if (!unitPrice) return;

    const priceEl = this.querySelector('[ref="priceContainer"] .price');
    if (!priceEl) return;

    const quantity = event.detail.quantity || 1;
    const total = unitPrice * quantity;
    priceEl.innerHTML = this.formatMoney(total);
  };

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '₹{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split('.');
    return format
      .replace('{{amount}}', `${whole}.${decimals}`)
      .replace('{{ amount }}', `${whole}.${decimals}`)
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{ amount_no_decimals }}', whole);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}